import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import { AppError, MarketSelector, ProductType } from "@obg/ngx.core";
import { RouteSelector } from "@obg/ngx.router";
import { of } from "rxjs";
import { catchError, concatMap, map, switchMap, withLatestFrom } from "rxjs/operators";

import { RegistrationFeatureState } from "../../state";
import { RegistrationAction } from "../registration.action";
import { RegistrationResponse } from "../registration.model";

import { AAMS_REGISTRATION_ACTION_TYPE, AamsRegistrationAction } from "./aams-registration.action";
import { AamsRegistrationPayload, AamsSetRegistrationDetailsPayload } from "./aams-registration.model";
import { AamsRegistrationService } from "./aams-registration.service";

@Injectable()
export class AamsRegistrationEffect {
	getRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AAMS_REGISTRATION_ACTION_TYPE.getRegistrationToken),
			map((x: ActionWithPayload<AamsRegistrationPayload>) => x.payload),
			concatMap((action) =>
				of(action).pipe(
					withLatestFrom(
						this.store.select(this.marketSelector.getCurrent()),
						this.store.select<{ product: ProductType | undefined } | undefined>(
							RouteSelector.getQueryParams
						)
					)
				)
			),
			switchMap(([request, market, params]) =>
				this.service.register(request, market, params?.product).pipe(
					map((response: RegistrationResponse) =>
						AamsRegistrationAction.getRegistrationTokenSuccess(response, request.credential)
					),
					catchError((response: AppError) => of(AamsRegistrationAction.getRegistrationTokenFail(response)))
				)
			)
		)
	);

	setRegistrationDetails$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AAMS_REGISTRATION_ACTION_TYPE.setRegistrationDetails),
			switchMap((x: ActionWithPayload<AamsSetRegistrationDetailsPayload>) => [
				this.registrationAction.setRegistrationDetails(x.payload.registrationDetails),
				this.registrationAction.setInitialStep(x.payload.initialStep),
			])
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<RegistrationFeatureState & CommonState>,
		private service: AamsRegistrationService,
		private marketSelector: MarketSelector,
		private registrationAction: RegistrationAction
	) {}
}
